import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Play, FileText, Clock, TrendingUp, AlertCircle, RefreshCw, WifiOff, Activity, CheckCircle, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWorkflows } from '../../hooks/useWorkflows';
import { useExecutions } from '../../hooks/useExecutions';
import { useToast } from '../../hooks/use-toast';
import { DataTable } from '../../components/ui/data-table';
import { columns } from '../History/columns';
import { Badge } from '../../components/ui/badge';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../components/ui/hover-card';

export function LiveDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Fetch real data from the backend
  const { data: workflows, isLoading: workflowsLoading, error: workflowsError } = useWorkflows();
  const { data: executionsData, isLoading: executionsLoading, error: executionsError } = useExecutions(10, 0);

  const executions = executionsData?.items || [];

  // Calculate statistics from real data
  const stats = {
    totalWorkflows: workflows?.length || 0,
    activeExecutions: executions.filter(e => e.status === 'running').length,
    completedToday: executions.filter(e => {
      const today = new Date().toDateString();
      return e.completed_at && new Date(e.completed_at).toDateString() === today;
    }).length,
    successRate: executions.length > 0 
      ? Math.round((executions.filter(e => e.status === 'completed').length / executions.length) * 100)
      : 0,
  };

  // Transform executions data for DataTable
  const tableData = executions.map(execution => ({
    execution_id: execution.execution_id,
    workflow_id: execution.workflow_id,
    status: execution.status,
    steps_completed: execution.steps_completed,
    steps_total: execution.steps_total,
    started_at: execution.started_at,
    completed_at: execution.completed_at,
    duration: execution.duration,
  }));

  // Show loading state
  if (workflowsLoading || executionsLoading) {
    return (
      <div className="space-y-6">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              Dashboard
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Loading dashboard data...
            </p>
          </div>
        </div>

        {/* Loading skeleton */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 rounded w-1/2 mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Show error state with enhanced error handling
  if (workflowsError || executionsError) {
    const handleRetry = () => {
      toast({
        title: 'Retrying connection...',
        description: 'Attempting to reconnect to the backend',
      });
      window.location.reload();
    };

    const isNetworkError = workflowsError?.message?.includes('Network Error') ||
                          executionsError?.message?.includes('Network Error') ||
                          workflowsError?.message?.includes('fetch') ||
                          executionsError?.message?.includes('fetch');

    return (
      <div className="space-y-6">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
              Dashboard - Connection Issue
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Unable to load dashboard data - please check your connection
            </p>
          </div>
        </div>

        <Card className="border-red-200 bg-gradient-to-br from-red-50 to-orange-50">
          <CardContent className="pt-6">
            <div className="text-center py-12">
              {isNetworkError ? (
                <WifiOff className="h-16 w-16 text-red-400 mx-auto mb-6 animate-pulse" />
              ) : (
                <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-6" />
              )}
              
              <h3 className="text-xl font-bold text-red-800 mb-3">
                {isNetworkError ? 'Network Connection Error' : 'Failed to Load Dashboard Data'}
              </h3>
              
              <div className="max-w-md mx-auto space-y-4">
                <p className="text-sm text-red-700 bg-red-100 p-3 rounded-lg border border-red-200">
                  <strong>Error Details:</strong><br />
                  {workflowsError?.message || executionsError?.message || 'Unable to connect to the backend server'}
                </p>
                
                {isNetworkError && (
                  <div className="text-sm text-orange-700 bg-orange-100 p-3 rounded-lg border border-orange-200">
                    <strong>Troubleshooting Tips:</strong>
                    <ul className="list-disc list-inside mt-2 space-y-1 text-left">
                      <li>Check your internet connection</li>
                      <li>Verify the backend server is running</li>
                      <li>Check if there are any firewall restrictions</li>
                    </ul>
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={handleRetry}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry Connection
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => toast({
                      title: 'Check Network',
                      description: 'Please verify your network connection and backend server status',
                    })}
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  >
                    <WifiOff className="h-4 w-4 mr-2" />
                    Check Network
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Monitor your workflows and track execution progress
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Button onClick={() => navigate('/execute')}>
            <Play className="h-4 w-4 mr-2" />
            Execute Workflow
          </Button>
        </div>
      </div>

      {/* Connection Status */}
      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="h-2 w-2 bg-green-500 rounded-full"></div>
            <div className="absolute inset-0 h-2 w-2 bg-green-400 rounded-full animate-ping"></div>
          </div>
          <span className="font-medium">
            Connected to backend API • {workflows?.length || 0} workflows available
          </span>
        </div>
        <Badge variant="outline" className="ml-auto bg-green-100 text-green-700 border-green-200">
          Live
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="flex items-center space-x-2">
              <CardTitle className="text-sm font-medium">Total Workflows</CardTitle>
              <HoverCard>
                <HoverCardTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Workflow Templates</h4>
                    <p className="text-sm">
                      Number of available workflow templates that can be executed.
                      Includes both system-provided and user-created workflows.
                    </p>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalWorkflows}</div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Available workflows</p>
              <Badge variant="outline" className="text-xs">
                Live
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="flex items-center space-x-2">
              <CardTitle className="text-sm font-medium">Active Executions</CardTitle>
              <HoverCard>
                <HoverCardTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Active Executions</h4>
                    <p className="text-sm">
                      Number of workflows currently being executed in real-time.
                      These are actively running processes that are consuming system resources.
                    </p>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeExecutions}</div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Currently running</p>
              <Badge variant={stats.activeExecutions > 0 ? "default" : "secondary"} className="text-xs">
                {stats.activeExecutions > 0 ? "Active" : "Idle"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="flex items-center space-x-2">
              <CardTitle className="text-sm font-medium">Completed Today</CardTitle>
              <HoverCard>
                <HoverCardTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Completed Today</h4>
                    <p className="text-sm">
                      Number of workflows that have successfully completed execution today.
                      This metric helps track daily productivity and system usage.
                    </p>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completedToday}</div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Successful executions</p>
              <Badge variant="outline" className="text-xs">
                Today
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div className="flex items-center space-x-2">
              <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
              <HoverCard>
                <HoverCardTrigger asChild>
                  <Info className="h-3 w-3 text-muted-foreground cursor-help" />
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Success Rate</h4>
                    <p className="text-sm">
                      Percentage of workflows that completed successfully versus those that failed.
                      Calculated based on the last 7 days of execution data.
                    </p>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.successRate}%</div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">Recent executions</p>
              <Badge variant={stats.successRate >= 90 ? "default" : "destructive"} className="text-xs">
                {stats.successRate >= 90 ? "Excellent" : "Needs Attention"}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Executions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Executions</CardTitle>
          <CardDescription>
            Latest workflow execution results and status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tableData.length > 0 ? (
            <DataTable
              columns={columns}
              data={tableData}
              searchPlaceholder="Search executions..."
              searchColumn="workflow_id"
            />
          ) : (
            <div className="text-center py-8">
              <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No executions yet
              </h3>
              <p className="text-sm text-gray-500">
                Start by executing a workflow to see activity here
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Workflows */}
      <Card>
        <CardHeader>
          <CardTitle>Available Workflows</CardTitle>
          <CardDescription>
            Migrated workflows ready for execution
          </CardDescription>
        </CardHeader>
        <CardContent>
          {workflows && workflows.length > 0 ? (
            <div className="space-y-4">
              {workflows.map((workflow) => (
                <div
                  key={workflow.workflow_id}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <FileText className="h-8 w-8 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">
                          {workflow.name}
                        </p>
                        <Badge variant="outline" className="text-xs">
                          {workflow.steps_count} steps
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {workflow.description}
                      </p>
                      <div className="flex items-center space-x-4 mt-2">
                        <span className="text-xs text-muted-foreground">
                          Est. {workflow.estimated_duration}
                        </span>
                        <div className="flex space-x-1">
                          {workflow.tags.map((tag, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      onClick={() => navigate(`/execute?workflow=${workflow.workflow_id}`)}
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Execute
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/workflows/${workflow.workflow_id}`)}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                No workflows available
              </h3>
              <p className="text-sm text-muted-foreground">
                Import or create workflows to get started
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common tasks and shortcuts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Button
              variant="outline"
              onClick={() => navigate('/execute')}
              className="h-20 flex-col"
            >
              <Play className="h-6 w-6 mb-2" />
              Execute Workflow
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/scripts')}
              className="h-20 flex-col"
            >
              <FileText className="h-6 w-6 mb-2" />
              Manage Scripts
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/history')}
              className="h-20 flex-col"
            >
              <Clock className="h-6 w-6 mb-2" />
              View History
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}